<template>
  <div v-if="showInstallPrompt" class="install-prompt">
    <div class="install-prompt-content">
      <div class="install-prompt-header">
        <span class="app-icon">🌱</span>
        <h3>Install PlantCare</h3>
      </div>
      <p>Add PlantCare to your home screen for better experience, offline access, and plant care reminders!</p>
      <div class="install-prompt-benefits">
        <div class="benefit-item">
          <span class="benefit-icon">⚡</span>
          <span>Faster access</span>
        </div>
        <div class="benefit-item">
          <span class="benefit-icon">📱</span>
          <span>App-like experience</span>
        </div>
        <div class="benefit-item">
          <span class="benefit-icon">🔔</span>
          <span>Get notifications</span>
        </div>
      </div>
      <div class="install-prompt-buttons">
        <button @click="installApp" class="install-button">Install Now</button>
        <button @click="dismissPrompt" class="dismiss-button">Maybe Later</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';

const deferredPrompt = ref(null);
const showInstallPrompt = ref(false);

console.log('[PWA] InstallPrompt component initialized');

// When beforeinstallprompt becomes available, show the prompt if appropriate
const handlePromptAvailable = (event) => {
  console.log('[PWA] Prompt event received in component through callback');
  
  deferredPrompt.value = event;
  checkAndShowPrompt();
};

// Listen for app installed event
window.addEventListener('appinstalled', () => {
  console.log('[PWA] App was installed successfully');
  showInstallPrompt.value = false;
  localStorage.setItem('pwaInstalled', 'true');
});

onMounted(() => {
  console.log('[PWA] InstallPrompt component mounted');
  
  // Check if already in standalone mode (already installed)
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                      (window.navigator.standalone === true);
  
  if (isStandalone) {
    console.log('[PWA] App running in standalone mode (already installed)');
    localStorage.setItem('pwaInstalled', 'true');
    return;
  }
  
  // Always check if we should show the prompt (with time throttling)
  // This applies to both native and fallback prompts
  
  // If we already have the install prompt, use it directly
  if (window.PWAInstall && window.PWAInstall.deferredPrompt) {
    console.log('[PWA] Prompt already available at mount time');
    deferredPrompt.value = window.PWAInstall.deferredPrompt;
  }
  
  // Always check and show prompt (throttling will be applied inside)
  checkAndShowPrompt();
  
  // Also register a callback for when native prompt becomes available later
  console.log('[PWA] Registering callback for when prompt becomes available');
  if (window.PWAInstall) {
    window.PWAInstall.onPromptAvailable(handlePromptAvailable);
  }
});

// Check if we should show the prompt (with time throttling)
const checkAndShowPrompt = () => {
  // Check if PWA is already installed (localStorage)
  const isPwaInstalled = localStorage.getItem('pwaInstalled') === 'true';
  
  if (isPwaInstalled) {
    console.log('[PWA] PWA already installed (localStorage), not showing prompt');
    return;
  }
  
  // Check if running in PWA/standalone mode
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                      (window.navigator.standalone === true);
  
  if (isStandalone) {
    console.log('[PWA] Already running in standalone mode, not showing prompt');
    localStorage.setItem('pwaInstalled', 'true'); // Update localStorage
    return;
  }
  
  // Check if browser supports PWA installation (basic support check)
  const supportsPWA = 'serviceWorker' in navigator;
  
  if (!supportsPWA) {
    console.log('[PWA] Browser does not support service workers, not showing prompt');
    return;
  }
  
  // Note: Even if no native deferred prompt, we can still show fallback instructions
  
  // Time-based throttling: only show once per hour
  const lastShownTime = localStorage.getItem('pwaPromptLastShown');
  const now = Date.now();
  const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds
  
  if (lastShownTime) {
    const timeSinceLastShown = now - parseInt(lastShownTime);
    if (timeSinceLastShown < oneHour) {
      const minutesRemaining = Math.ceil((oneHour - timeSinceLastShown) / (60 * 1000));
      console.log(`[PWA] PWA prompt throttled. Will show again in ${minutesRemaining} minutes`);
      return;
    }
  }
  
  console.log('[PWA] All checks passed - showing install prompt');
  console.log('[PWA] Native prompt available:', !!deferredPrompt.value);
  
  // Store the current time as the last shown time
  localStorage.setItem('pwaPromptLastShown', now.toString());
  
  // Show install prompt after a short delay
  setTimeout(() => {
    if (deferredPrompt.value) {
      console.log('[PWA] Showing native install prompt');
    } else {
      console.log('[PWA] Showing fallback install prompt (no native prompt available)');
    }
    showInstallPrompt.value = true;
  }, 1500);
};

const installApp = async () => {
  console.log('[PWA] Install button clicked');
  console.log('[PWA] deferredPrompt available:', !!deferredPrompt.value);
  console.log('[PWA] window.PWAInstall:', window.PWAInstall);
  
  if (deferredPrompt.value) {
    // Native installation available
    try {
      console.log('[PWA] Using native installation prompt');
      console.log('[PWA] Calling prompt() method...');
      
      deferredPrompt.value.prompt();
      
      // Wait for user response
      console.log('[PWA] Waiting for user choice...');
      const { outcome } = await deferredPrompt.value.userChoice;
      console.log(`[PWA] User response to native install prompt: ${outcome}`);
      
      // Reset the deferred prompt
      deferredPrompt.value = null;
      if (window.PWAInstall) {
        window.PWAInstall.deferredPrompt = null;
      }
      
      // Hide our custom UI
      showInstallPrompt.value = false;
      
      if (outcome === 'accepted') {
        localStorage.setItem('pwaInstalled', 'true');
        console.log('[PWA] Installation accepted, marking as installed');
      } else {
        console.log('[PWA] Installation declined by user');
      }
    } catch (error) {
      console.error('[PWA] Native installation failed:', error);
      console.error('[PWA] Error details:', error.message, error.stack);
      // Fall through to fallback method
      showFallbackInstructions();
    }
  } else {
    // No native prompt available, show fallback instructions
    console.log('[PWA] No deferred prompt available, using fallback instructions');
    console.log('[PWA] Checking window.PWAInstall.deferredPrompt:', window.PWAInstall?.deferredPrompt);
    
    // Try to get the prompt from window.PWAInstall if available
    if (window.PWAInstall && window.PWAInstall.deferredPrompt) {
      console.log('[PWA] Found deferred prompt in window.PWAInstall, trying that...');
      deferredPrompt.value = window.PWAInstall.deferredPrompt;
      // Recursively call installApp now that we have the prompt
      return installApp();
    } else {
      console.log('[PWA] Using fallback installation instructions');
      showFallbackInstructions();
    }
  }
};

const showFallbackInstructions = () => {
  // Use the enhanced fallback prompt from main.js
  if (window.PWAInstall && window.PWAInstall.showFallbackPrompt) {
    showInstallPrompt.value = false; // Hide the component prompt
    window.PWAInstall.showFallbackPrompt();
  } else {
    // Simple fallback if main.js function not available
    const ua = navigator.userAgent;
    let instructions = '';
    
    if (/iPhone|iPad|iPod/i.test(ua)) {
      instructions = 'To install:\n\n1. Tap the Share button (□↗)\n2. Tap "Add to Home Screen"\n3. Tap "Add"';
    } else if (/Android/i.test(ua)) {
      instructions = 'To install:\n\n1. Tap the menu (⋮)\n2. Tap "Install app" or "Add to Home screen"';
    } else {
      instructions = 'To install:\n\n1. Look for the install (+) icon in your address bar\n2. Or check your browser menu for install options';
    }
    
    alert(instructions);
    showInstallPrompt.value = false;
  }
};

const dismissPrompt = () => {
  showInstallPrompt.value = false;
  // Update the last shown time so user won't see it again for an hour
  localStorage.setItem('pwaPromptLastShown', Date.now().toString());
  console.log('[PWA] Install prompt dismissed - will show again in 1 hour');
};
</script>

<style scoped>
.install-prompt {
  position: fixed;
  bottom: 20px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  z-index: 9999;
}

.install-prompt-content {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 400px;
  text-align: center;
  border-left: 5px solid var(--primary-color);
  animation: slideUp 0.4s ease-out;
}

.install-prompt-header {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
}

.app-icon {
  font-size: 24px;
  margin-right: 10px;
}

.install-prompt-header h3 {
  margin: 0;
  color: var(--primary-color);
  font-weight: bold;
}

.install-prompt-content p {
  margin: 8px 0 16px;
  color: #333;
  font-size: 14px;
  line-height: 1.5;
}

.install-prompt-benefits {
  display: flex;
  justify-content: space-between;
  margin: 15px 0;
  padding: 10px;
  background-color: rgba(76, 175, 80, 0.08);
  border-radius: 8px;
}

.benefit-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 12px;
  padding: 0 5px;
}

.benefit-icon {
  font-size: 18px;
  margin-bottom: 5px;
}

.install-prompt-buttons {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 15px;
}

.install-button {
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
}

.install-button:hover {
  background-color: var(--primary-dark);
  transform: translateY(-2px);
}

.dismiss-button {
  background-color: transparent;
  color: #666;
  border: 1px solid #ddd;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.2s;
}

.dismiss-button:hover {
  background-color: #f5f5f5;
}

@keyframes slideUp {
  from {
    transform: translateY(30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@media (max-width: 480px) {
  .install-prompt-content {
    width: 95%;
    max-width: none;
    padding: 15px;
  }
  
  .install-prompt-benefits {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }
  
  .benefit-item {
    flex-direction: row;
    align-items: center;
  }
  
  .benefit-icon {
    margin-right: 10px;
    margin-bottom: 0;
  }
}
</style>